import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react'
import { CartItem, Product, APP_CONFIG } from '../types'

interface CartContextType {
    items: CartItem[]
    addItem: (product: Product, quantity?: number) => void
    removeItem: (productId: string) => void
    updateQuantity: (productId: string, quantity: number) => void
    clearCart: () => void
    getItemCount: () => number
    getSubtotal: () => number
    getSCoinsValue: (scoins: number) => number
    getTotal: (scoinsToUse: number) => number
    getSCoinsEarned: (total: number) => number
}

const CartContext = createContext<CartContextType | undefined>(undefined)

const CART_STORAGE_KEY = 'sm_cart'

export function CartProvider({ children }: { children: ReactNode }) {
    const [items, setItems] = useState<CartItem[]>(() => {
        const saved = localStorage.getItem(CART_STORAGE_KEY)
        if (saved) {
            try {
                return JSON.parse(saved)
            } catch {
                return []
            }
        }
        return []
    })

    // Persist cart to localStorage
    useEffect(() => {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items))
    }, [items])

    const addItem = useCallback((product: Product, quantity: number = 1) => {
        setItems(prev => {
            const existing = prev.find(item => item.product.id === product.id)
            if (existing) {
                return prev.map(item =>
                    item.product.id === product.id
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                )
            }
            return [...prev, { product, quantity }]
        })
    }, [])

    const removeItem = useCallback((productId: string) => {
        setItems(prev => prev.filter(item => item.product.id !== productId))
    }, [])

    const updateQuantity = useCallback((productId: string, quantity: number) => {
        if (quantity <= 0) {
            removeItem(productId)
            return
        }
        setItems(prev =>
            prev.map(item =>
                item.product.id === productId ? { ...item, quantity } : item
            )
        )
    }, [removeItem])

    const clearCart = useCallback(() => {
        setItems([])
    }, [])

    const getItemCount = useCallback(() => {
        return items.reduce((sum, item) => sum + item.quantity, 0)
    }, [items])

    const getSubtotal = useCallback(() => {
        return items.reduce((sum, item) => sum + item.product.sm_price * item.quantity, 0)
    }, [items])

    const getSCoinsValue = useCallback((scoins: number) => {
        // 100 S-Coins = 10 INR discount
        return Math.floor(scoins / 100) * APP_CONFIG.SCOINS_REDEEM_RATE
    }, [])

    const getTotal = useCallback((scoinsToUse: number) => {
        const subtotal = getSubtotal()
        const discount = getSCoinsValue(scoinsToUse)
        return Math.max(0, subtotal - discount)
    }, [getSubtotal, getSCoinsValue])

    const getSCoinsEarned = useCallback((total: number) => {
        // Earn 10 S-Coins per ₹100 spent
        return Math.floor(total / 100) * APP_CONFIG.SCOINS_EARN_RATE
    }, [])

    return (
        <CartContext.Provider value={{
            items,
            addItem,
            removeItem,
            updateQuantity,
            clearCart,
            getItemCount,
            getSubtotal,
            getSCoinsValue,
            getTotal,
            getSCoinsEarned
        }}>
            {children}
        </CartContext.Provider>
    )
}

export function useCart() {
    const context = useContext(CartContext)
    if (!context) {
        throw new Error('useCart must be used within CartProvider')
    }
    return context
}
